export function hexToRgb(
  hex: string
): { r: number; g: number; b: number } | null {
  const cleanHex = hex.replace('#', '');

  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return { r, g, b };
  }

  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return { r, g, b };
  }

  return null;
}

export function getRelativeLuminance(r: number, g: number, b: number): number {
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  const rLinear =
    rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear =
    gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear =
    bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

export function getContrastColor(hexColor: string): string {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return '#000000';

  return getContrastColorFromRgb(rgb.r, rgb.g, rgb.b);
}

export function getContrastColorFromRgb(
  r: number,
  g: number,
  b: number
): string {
  const luminance = getRelativeLuminance(r, g, b);
  return luminance > 0.179 ? '#000000' : '#FFFFFF';
}

export function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

export function addAlphaToRgb(rgb: string, alpha: number): string {
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (match) {
    return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
  }
  return rgb;
}

export function applyOpacityToGradient(
  gradient: string,
  alpha: number
): string {
  let result = gradient.replace(
    /#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})\b/g,
    (match) => hexToRgba(match, alpha)
  );

  result = result.replace(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/gi,
    (match, r, g, b) => `rgba(${r}, ${g}, ${b}, ${alpha})`
  );

  return result;
}

export function blendColors(
  foreground: { r: number; g: number; b: number },
  background: { r: number; g: number; b: number },
  opacity: number
): { r: number; g: number; b: number } {
  return {
    r: Math.round(foreground.r * opacity + background.r * (1 - opacity)),
    g: Math.round(foreground.g * opacity + background.g * (1 - opacity)),
    b: Math.round(foreground.b * opacity + background.b * (1 - opacity)),
  };
}

export function parseRgb(
  rgbString: string
): { r: number; g: number; b: number } | null {
  const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    return {
      r: parseInt(match[1]),
      g: parseInt(match[2]),
      b: parseInt(match[3]),
    };
  }
  return null;
}

export function parseColor(
  colorString: string
): { r: number; g: number; b: number } | null {
  const trimmed = colorString.trim();

  if (trimmed.startsWith('#')) {
    return hexToRgb(trimmed);
  }

  if (trimmed.startsWith('rgb')) {
    return parseRgb(trimmed);
  }

  return null;
}

export function extractColorsFromGradient(
  gradient: string
): { r: number; g: number; b: number } | null {
  const colors: Array<{ r: number; g: number; b: number }> = [];

  const colorNames: Record<string, { r: number; g: number; b: number }> = {
    white: { r: 255, g: 255, b: 255 },
    black: { r: 0, g: 0, b: 0 },
  };

  const hexMatches = gradient.matchAll(/#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3}/g);
  for (const match of hexMatches) {
    const rgb = hexToRgb(match[0]);
    if (rgb) colors.push(rgb);
  }

  const rgbMatches = gradient.matchAll(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/g
  );
  for (const match of rgbMatches) {
    colors.push({
      r: parseInt(match[1]),
      g: parseInt(match[2]),
      b: parseInt(match[3]),
    });
  }

  const colorNameMatches = gradient.matchAll(/\b(white|black)\b/gi);
  for (const match of colorNameMatches) {
    const color = match[0].toLowerCase();
    colors.push(colorNames[color]);
  }

  if (colors.length === 0) return null;

  const avgR = Math.round(
    colors.reduce((sum, c) => sum + c.r, 0) / colors.length
  );
  const avgG = Math.round(
    colors.reduce((sum, c) => sum + c.g, 0) / colors.length
  );
  const avgB = Math.round(
    colors.reduce((sum, c) => sum + c.b, 0) / colors.length
  );

  return { r: avgR, g: avgG, b: avgB };
}

export interface RegionColorSample {
  average: { r: number; g: number; b: number };
  darkest: { r: number; g: number; b: number };
  lightest: { r: number; g: number; b: number };
}

export type ImageColorRange = RegionColorSample;

const IMAGE_CACHE_MAX_ENTRIES = 12;

function readImageCache<T>(cache: Map<string, T>, key: string): T | undefined {
  const value = cache.get(key);
  if (value === undefined) return undefined;

  cache.delete(key);
  cache.set(key, value);

  return value;
}

function writeImageCache<T>(
  cache: Map<string, T>,
  key: string,
  value: T
): void {
  cache.delete(key);
  cache.set(key, value);

  while (cache.size > IMAGE_CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) return;
    cache.delete(oldest);
  }
}

const imageColorRangeCache = new Map<string, ImageColorRange>();

const imageColorFailureCache = new Map<string, ImageColorRange>();

const inFlightImageColorRangeRequests = new Map<
  string,
  Promise<ImageColorRange>
>();

function buildFlatRange(rgb: {
  r: number;
  g: number;
  b: number;
}): ImageColorRange {
  return { average: rgb, darkest: rgb, lightest: rgb };
}

const imageElementCache = new Map<string, HTMLImageElement>();

export function releaseImageColor(imageUrl: string): void {
  imageColorRangeCache.delete(imageUrl);
  imageColorFailureCache.delete(imageUrl);
  inFlightImageColorRangeRequests.delete(imageUrl);
  imageElementCache.delete(imageUrl);
}

// test-exception: `Image` decoding is not implemented by jsdom, so neither the
// load nor the error path can be exercised in isolation.
export function getImageElement(imageUrl: string): Promise<HTMLImageElement> {
  const cached = readImageCache(imageElementCache, imageUrl);
  if (cached) return Promise.resolve(cached);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.addEventListener('load', () => {
      writeImageCache(imageElementCache, imageUrl, img);
      resolve(img);
    });

    img.addEventListener('error', () =>
      reject(new Error(`Failed to load image: ${imageUrl}`))
    );

    img.src = imageUrl;
  });
}

export function computeCoverSourceRect(
  naturalWidth: number,
  naturalHeight: number,
  containerWidth: number,
  containerHeight: number
): { sx: number; sy: number; sw: number; sh: number } {
  const imageRatio = naturalWidth / naturalHeight;
  const containerRatio = containerWidth / containerHeight;

  if (imageRatio > containerRatio) {
    const sh = naturalHeight;
    const sw = sh * containerRatio;
    return { sx: (naturalWidth - sw) / 2, sy: 0, sw, sh };
  }

  const sw = naturalWidth;
  const sh = sw / containerRatio;
  return { sx: 0, sy: (naturalHeight - sh) / 2, sw, sh };
}

export interface RegionSampler {
  sampleRegion(rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): RegionColorSample | null;
}

function rec709LumaOnRawSrgbChannels(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function buildSrgbLinearLookup(): Float64Array {
  const lookup = new Float64Array(256);

  for (let value = 0; value < 256; value += 1) {
    const channel = value / 255;
    lookup[value] =
      channel <= 0.03928
        ? channel / 12.92
        : Math.pow((channel + 0.055) / 1.055, 2.4);
  }

  return lookup;
}

const SRGB_LINEAR_LOOKUP = buildSrgbLinearLookup();

function lookupRelativeLuminance(r: number, g: number, b: number): number {
  return (
    0.2126 * SRGB_LINEAR_LOOKUP[r] +
    0.7152 * SRGB_LINEAR_LOOKUP[g] +
    0.0722 * SRGB_LINEAR_LOOKUP[b]
  );
}

const REGION_SAMPLE_TARGET_PIXELS = 2000;

// test-exception: needs a real canvas 2D context and `getImageData`, neither of
// which jsdom implements. Its two pure pieces — the cover crop geometry and the
// gradient equivalent — are covered by `computeCoverSourceRect` and
// `sampleLinearGradientRegion` above.
export function createCoverImageSampler(
  img: HTMLImageElement,
  containerSize: { width: number; height: number },
  baseColor: string | null
): RegionSampler | null {
  if (containerSize.width <= 0 || containerSize.height <= 0) return null;

  const width = Math.max(1, Math.round(containerSize.width));
  const height = Math.max(1, Math.round(containerSize.height));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  if (baseColor) {
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, 0, width, height);
  }

  const { sx, sy, sw, sh } = computeCoverSourceRect(
    img.naturalWidth,
    img.naturalHeight,
    width,
    height
  );
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);

  let data: Uint8ClampedArray;
  try {
    ({ data } = ctx.getImageData(0, 0, width, height));
  } catch {
    return null;
  }

  return createPixelRegionSampler({ data, width, height });
}

interface SampledPixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

function createPixelRegionSampler(pixels: SampledPixels): RegionSampler {
  return {
    sampleRegion(rect) {
      const x = Math.max(0, Math.floor(rect.x));
      const y = Math.max(0, Math.floor(rect.y));
      const visibleWidth = rect.width + Math.min(0, rect.x);
      const visibleHeight = rect.height + Math.min(0, rect.y);
      const regionWidth = Math.min(Math.ceil(visibleWidth), pixels.width - x);
      const regionHeight = Math.min(
        Math.ceil(visibleHeight),
        pixels.height - y
      );
      if (regionWidth <= 0 || regionHeight <= 0) return null;

      return scanRegionExtremes({
        data: pixels.data,
        imageWidth: pixels.width,
        x,
        y,
        width: regionWidth,
        height: regionHeight,
      });
    },
  };
}

export interface RegionPixelScan {
  data: Uint8ClampedArray;
  imageWidth: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export function scanRegionExtremes({
  data,
  imageWidth,
  x,
  y,
  width,
  height,
}: RegionPixelScan): RegionColorSample | null {
  if (width <= 0 || height <= 0) return null;

  const stride = Math.max(
    1,
    Math.round(Math.sqrt((width * height) / REGION_SAMPLE_TARGET_PIXELS))
  );

  let totalR = 0;
  let totalG = 0;
  let totalB = 0;
  let sampled = 0;
  let darkestLuminance = Number.POSITIVE_INFINITY;
  let lightestLuminance = Number.NEGATIVE_INFINITY;
  let darkest = { r: 0, g: 0, b: 0 };
  let lightest = { r: 0, g: 0, b: 0 };

  for (let row = 0; row < height; row += stride) {
    for (let column = 0; column < width; column += stride) {
      const index = ((y + row) * imageWidth + (x + column)) * 4;
      const r = data[index];
      const g = data[index + 1];
      const b = data[index + 2];

      totalR += r;
      totalG += g;
      totalB += b;
      sampled += 1;

      const luminance = lookupRelativeLuminance(r, g, b);
      if (luminance < darkestLuminance) {
        darkestLuminance = luminance;
        darkest = { r, g, b };
      }
      if (luminance > lightestLuminance) {
        lightestLuminance = luminance;
        lightest = { r, g, b };
      }
    }
  }

  if (sampled === 0) return null;

  return {
    average: {
      r: Math.round(totalR / sampled),
      g: Math.round(totalG / sampled),
      b: Math.round(totalB / sampled),
    },
    darkest,
    lightest,
  };
}

function clampGradientPosition(position: number): number {
  return Math.min(1, Math.max(0, position));
}

export interface LinearGradientStop {
  color: string;
  position: number;
}

interface ResolvedGradientStop {
  rgb: { r: number; g: number; b: number };
  position: number;
}

function resolveGradientStops(
  stops: readonly LinearGradientStop[]
): ResolvedGradientStop[] {
  return stops.flatMap((stop) => {
    const rgb = parseColor(stop.color);

    if (!rgb) return [];

    return [{ rgb, position: clampGradientPosition(stop.position) }];
  });
}

function colorAtGradientPosition(
  stops: readonly ResolvedGradientStop[],
  position: number
): { r: number; g: number; b: number } {
  const first = stops[0];
  const last = stops[stops.length - 1];

  if (position <= first.position) return first.rgb;
  if (position >= last.position) return last.rgb;

  for (let index = 1; index < stops.length; index += 1) {
    const previous = stops[index - 1];
    const current = stops[index];

    if (position > current.position) continue;

    const span = current.position - previous.position;

    if (span <= 0) return current.rgb;

    return blendColors(
      current.rgb,
      previous.rgb,
      (position - previous.position) / span
    );
  }

  return last.rgb;
}

function collectPiecewiseLinearExtremePositions(
  stops: readonly ResolvedGradientStop[],
  start: number,
  end: number
): number[] {
  const positions = [start, end];

  stops.forEach((stop) => {
    if (stop.position > start && stop.position < end) {
      positions.push(stop.position);
    }
  });

  return positions;
}

function sampleGradientStops(
  stops: readonly LinearGradientStop[],
  positionRange: { start: number; end: number; center: number }
): RegionColorSample | null {
  const resolved = resolveGradientStops(stops);

  if (resolved.length < 2) return null;

  const sorted = resolved.toSorted((a, b) => a.position - b.position);

  let darkest = colorAtGradientPosition(sorted, positionRange.start);
  let lightest = darkest;
  let darkestLuminance = lookupRelativeLuminance(
    darkest.r,
    darkest.g,
    darkest.b
  );
  let lightestLuminance = darkestLuminance;

  collectPiecewiseLinearExtremePositions(
    sorted,
    positionRange.start,
    positionRange.end
  ).forEach((position) => {
    const rgb = colorAtGradientPosition(sorted, position);
    const luminance = lookupRelativeLuminance(rgb.r, rgb.g, rgb.b);

    if (luminance < darkestLuminance) {
      darkestLuminance = luminance;
      darkest = rgb;
    }
    if (luminance > lightestLuminance) {
      lightestLuminance = luminance;
      lightest = rgb;
    }
  });

  return {
    average: colorAtGradientPosition(sorted, positionRange.center),
    darkest,
    lightest,
  };
}

export function sampleLinearGradientRegion(
  gradient: {
    from: string;
    to: string;
    angle: number;
    stops?: readonly LinearGradientStop[];
  },
  containerSize: { width: number; height: number },
  rectInContainerPx: { x: number; y: number; width: number; height: number }
): RegionColorSample | null {
  const from = parseColor(gradient.from);
  const to = parseColor(gradient.to);
  if (!from || !to) return null;
  if (containerSize.width <= 0 || containerSize.height <= 0) return null;

  const radians = (gradient.angle * Math.PI) / 180;
  const axisX = Math.sin(radians);
  const axisY = -Math.cos(radians);
  const lineLength =
    Math.abs(containerSize.width * axisX) +
    Math.abs(containerSize.height * axisY);

  if (lineLength <= 0) {
    return { average: from, darkest: from, lightest: from };
  }

  const centerX = containerSize.width / 2;
  const centerY = containerSize.height / 2;
  const positionAt = (x: number, y: number) =>
    clampGradientPosition(
      0.5 + ((x - centerX) * axisX + (y - centerY) * axisY) / lineLength
    );

  const right = rectInContainerPx.x + rectInContainerPx.width;
  const bottom = rectInContainerPx.y + rectInContainerPx.height;
  const corners = [
    positionAt(rectInContainerPx.x, rectInContainerPx.y),
    positionAt(right, rectInContainerPx.y),
    positionAt(rectInContainerPx.x, bottom),
    positionAt(right, bottom),
  ];

  if (gradient.stops && gradient.stops.length >= 2) {
    const sampled = sampleGradientStops(gradient.stops, {
      start: Math.min(...corners),
      end: Math.max(...corners),
      center: positionAt(
        rectInContainerPx.x + rectInContainerPx.width / 2,
        rectInContainerPx.y + rectInContainerPx.height / 2
      ),
    });

    if (sampled) return sampled;
  }

  const average = blendColors(
    to,
    from,
    positionAt(
      rectInContainerPx.x + rectInContainerPx.width / 2,
      rectInContainerPx.y + rectInContainerPx.height / 2
    )
  );
  const edgeStart = blendColors(to, from, Math.min(...corners));
  const edgeEnd = blendColors(to, from, Math.max(...corners));
  const isStartDarker =
    rec709LumaOnRawSrgbChannels(edgeStart.r, edgeStart.g, edgeStart.b) <=
    rec709LumaOnRawSrgbChannels(edgeEnd.r, edgeEnd.g, edgeEnd.b);

  return {
    average,
    darkest: isStartDarker ? edgeStart : edgeEnd,
    lightest: isStartDarker ? edgeEnd : edgeStart,
  };
}

const IMAGE_ANALYSIS_SAMPLE_SIZE = 100;

const IMAGE_ANALYSIS_SAMPLE_POINTS = 25;

// test-exception: jsdom implements neither `Image` decoding nor canvas reads
function analyzeImageColorRange(imageUrl: string): Promise<ImageColorRange> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    const resolveFailure = () => {
      const range = buildFlatRange({ r: 128, g: 128, b: 128 });
      writeImageCache(imageColorFailureCache, imageUrl, range);
      resolve(range);
    };

    img.addEventListener('load', () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (!ctx) {
          resolveFailure();
          return;
        }

        const sampleSize = IMAGE_ANALYSIS_SAMPLE_SIZE;
        canvas.width = sampleSize;
        canvas.height = sampleSize;

        ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

        const { data } = ctx.getImageData(0, 0, sampleSize, sampleSize);

        const step = sampleSize / Math.sqrt(IMAGE_ANALYSIS_SAMPLE_POINTS);
        let totalR = 0;
        let totalG = 0;
        let totalB = 0;
        let count = 0;
        let darkest = { r: 255, g: 255, b: 255 };
        let darkestLuminance = Infinity;
        let lightest = { r: 0, g: 0, b: 0 };
        let lightestLuminance = -Infinity;

        for (let y = step / 2; y < sampleSize; y += step) {
          for (let x = step / 2; x < sampleSize; x += step) {
            const index = (Math.floor(y) * sampleSize + Math.floor(x)) * 4;
            const sample = {
              r: data[index],
              g: data[index + 1],
              b: data[index + 2],
            };
            totalR += sample.r;
            totalG += sample.g;
            totalB += sample.b;
            count++;

            const luminance = getRelativeLuminance(
              sample.r,
              sample.g,
              sample.b
            );
            if (luminance < darkestLuminance) {
              darkestLuminance = luminance;
              darkest = sample;
            }
            if (luminance > lightestLuminance) {
              lightestLuminance = luminance;
              lightest = sample;
            }
          }
        }

        const range: ImageColorRange = {
          average: {
            r: Math.round(totalR / count),
            g: Math.round(totalG / count),
            b: Math.round(totalB / count),
          },
          darkest,
          lightest,
        };

        imageColorFailureCache.delete(imageUrl);
        writeImageCache(imageColorRangeCache, imageUrl, range);
        resolve(range);
      } catch {
        resolveFailure();
      }
    });

    img.addEventListener('error', resolveFailure);

    img.src = imageUrl;
  });
}

export async function getImageColorRange(
  imageUrl: string
): Promise<ImageColorRange> {
  if (typeof window === 'undefined') {
    return buildFlatRange({ r: 80, g: 80, b: 80 });
  }

  const cached = readImageCache(imageColorRangeCache, imageUrl);
  if (cached) {
    return cached;
  }

  const pending = inFlightImageColorRangeRequests.get(imageUrl);
  if (pending) {
    return pending;
  }

  const request = analyzeImageColorRange(imageUrl).finally(() => {
    inFlightImageColorRangeRequests.delete(imageUrl);
  });
  inFlightImageColorRangeRequests.set(imageUrl, request);

  return request;
}

export function getImageColorRangeSync(imageUrl: string): ImageColorRange {
  if (typeof window === 'undefined') {
    return buildFlatRange({ r: 80, g: 80, b: 80 });
  }

  const cached = readImageCache(imageColorRangeCache, imageUrl);
  if (cached) {
    return cached;
  }

  const failed = readImageCache(imageColorFailureCache, imageUrl);
  if (failed) {
    return failed;
  }

  void getImageColorRange(imageUrl);

  return buildFlatRange({ r: 80, g: 80, b: 80 });
}

export async function getAverageImageColor(
  imageUrl: string
): Promise<{ r: number; g: number; b: number }> {
  return (await getImageColorRange(imageUrl)).average;
}

export function getImageColorSync(imageUrl: string): {
  r: number;
  g: number;
  b: number;
} {
  return getImageColorRangeSync(imageUrl).average;
}

export function calculateEffectiveBackgroundColor(
  backgroundColor: string | null,
  backgroundImage: string | null,
  mode: 'light' | 'dark'
): { r: number; g: number; b: number } {
  let effectiveColor = { r: 241, g: 241, b: 248 };

  if (backgroundImage) {
    effectiveColor = getImageColorSync(backgroundImage);

    if (backgroundColor) {
      let overlayColor: { r: number; g: number; b: number } | null;

      if (
        backgroundColor.includes('linear-gradient') ||
        backgroundColor.includes('radial-gradient')
      ) {
        overlayColor = extractColorsFromGradient(backgroundColor);
      } else {
        overlayColor = parseColor(backgroundColor);
      }

      if (overlayColor) {
        effectiveColor = blendColors(overlayColor, effectiveColor, 0.64);
      }
    }
  } else if (backgroundColor) {
    let baseColor: { r: number; g: number; b: number } | null;

    if (
      backgroundColor.includes('linear-gradient') ||
      backgroundColor.includes('radial-gradient')
    ) {
      baseColor = extractColorsFromGradient(backgroundColor);
    } else {
      baseColor = parseColor(backgroundColor);
    }

    if (baseColor) {
      effectiveColor = baseColor;
    }
  }

  const modeOverlay =
    mode === 'light' ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };
  const modeOpacity = mode === 'light' ? 0.1 : 0.3;

  effectiveColor = blendColors(modeOverlay, effectiveColor, modeOpacity);

  return effectiveColor;
}

export function getContrastTextColor(
  backgroundColor: string | null,
  backgroundImage: string | null,
  mode: 'light' | 'dark'
): string {
  const effectiveBg = calculateEffectiveBackgroundColor(
    backgroundColor,
    backgroundImage,
    mode
  );

  return getContrastColorFromRgb(effectiveBg.r, effectiveBg.g, effectiveBg.b);
}

export function getSystemMessageTextColor(
  backgroundColor: string | null,
  backgroundImage: string | null,
  mode: 'light' | 'dark',
  transparency: number
): string {
  const effectivePageBg = calculateEffectiveBackgroundColor(
    backgroundColor,
    backgroundImage,
    mode
  );

  const bubbleColor = { r: 255, g: 255, b: 255 };
  const bubbleOpacity = (transparency ?? 24) / 100;

  const effectiveBubbleBg = blendColors(
    bubbleColor,
    effectivePageBg,
    bubbleOpacity
  );

  return getContrastColorFromRgb(
    effectiveBubbleBg.r,
    effectiveBubbleBg.g,
    effectiveBubbleBg.b
  );
}

export function getContrastRatio(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number {
  const lum1 = getRelativeLuminance(color1.r, color1.g, color1.b);
  const lum2 = getRelativeLuminance(color2.r, color2.g, color2.b);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

export function preloadImageColor(
  imageUrl: string
): Promise<{ r: number; g: number; b: number }> {
  return getAverageImageColor(imageUrl);
}
